import { useState, useEffect } from 'react';
import axios from 'axios';
const API = 'https://api.nycni-chem-ai-class.top/api/student';
function StudentPanel() {
  const [status, setStatus] = useState({ active: false, activeTask: null });
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [studentId, setStudentId] = useState(() => {
    return localStorage.getItem('chem_student_id') || `student_${Math.random().toString(36).substr(2, 5)}`;
  });
  // 保存学生ID
  useEffect(() => {
    localStorage.setItem('chem_student_id', studentId);
  }, [studentId]);
  // 轮询课堂状态，收到新任务自动重置界面--
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/status`);
        // 收到新任务，清空历史记录
        if (res.data.activeTask && (!status.activeTask || res.data.activeTask.id !== status.activeTask?.id)) {
          setHistory([]);
        }
        setStatus(res.data);
      } catch (err) {
        console.error("连接后端失败", err);
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 2000);
    return () => clearInterval(interval);
  }, [status.activeTask]);
  // 提交处理
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setLoading(true);
    
    // 先把学生的输入加入历史
    const userMsg = { role: 'me', content: input };
    setHistory(prev => [...prev, userMsg]);
    const submitContent = input;
    setInput(''); // 清空输入框
    try {
      const res = await axios.post(`${API}/submit`, {
        content: submitContent,
        type: status.activeTask ? 'answer' : 'question',
        taskId: status.activeTask?.id,
        studentId: studentId
      });
      // 把AI回复加入历史
      const aiMsg = { 
        role: 'ai', 
        content: res.data.answer, 
        eval: res.data.evaluation 
      };
      setHistory(prev => [...prev, aiMsg]);
    } catch (err) {
      // 错误兜底
      const errMsg = { 
        role: 'ai', 
        content: err.response?.data?.error || "提交失败，请检查后端服务是否正常运行", 
        eval: null 
      };
      setHistory(prev => [...prev, errMsg]);
    }
    setLoading(false);
  };
  // 等级颜色映射
  const getLevelColor = (level) => {
    const colors = { 
      'A': 'bg-green-100 text-green-800 border-green-200', 
      'B': 'bg-blue-100 text-blue-800 border-blue-200', 
      'C': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'D': 'bg-orange-100 text-orange-800 border-orange-200',
      'E': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };
  return (
    <div className="max-w-3xl mx-auto p-4 h-[90vh] flex flex-col">
      {/* 顶部状态栏 */}
      <div className={`mb-6 p-5 rounded-2xl border ${status.active ? 'bg-green-50 border-green-200' : 'bg-gray-100 border-gray-200'}`}>
        {status.active ? (
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-xl font-bold text-green-800">📚 {status.classroomTitle}</h1>
              <p className="text-green-600 text-sm">{status.textbookVersion} {status.module}</p>
            </div>
            <div className="flex items-center gap-2">
               <span className="text-sm text-gray-500">我的ID:</span>
               <input 
                 value={studentId} 
                 onChange={(e) => setStudentId(e.target.value)}
                 className="w-24 text-center border rounded px-2 py-1 text-sm bg-white"
               />
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500 py-4">
            <div className="text-lg">等待老师开始上课...</div>
          </div>
        )}
      </div>
      {/* 任务卡片 */}
      {status.activeTask && (
        <div className="bg-white border-2 border-purple-400 rounded-2xl p-6 mb-6 shadow-lg">
          <div className="flex items-center gap-2 mb-3">
            <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-bold">📝 课堂任务</span>
          </div>
          {/* 唯一修改点：引导提问任务隐藏预设问题，显示固定文本 */}
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {status.activeTask.type === 'student_question' ? '根据课堂进度，你有什么问题吗？' : status.activeTask.question}
          </h2>
          {status.activeTask.materials && <p className="text-gray-500 text-sm">📖 参考: {status.activeTask.materials}</p>}
        </div>
      )}
      {/* 聊天/交互区域 */}
      <div className="flex-1 flex flex-col">
        {/* 历史记录 */}
        <div className="flex-1 overflow-y-auto space-y-6 mb-6">
          {history.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'me' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] ${msg.role === 'me' ? '' : 'w-full max-w-3xl'}`}>
                {msg.role === 'me' ? (
                  <div className="bg-blue-600 text-white px-6 py-3 rounded-2xl rounded-tr-md max-w-md shadow-md">
                    {msg.content}
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    <div className="p-6 text-gray-800 leading-relaxed whitespace-pre-wrap">
                      {msg.content}
                    </div>
                    
                    {msg.eval && (
                      <div className="bg-gradient-to-r from-gray-50 to-blue-50 border-t p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-xs font-bold text-gray-500 uppercase">核心素养评价</span>
                          <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getLevelColor(msg.eval.overallLevel)}`}>
                            综合等级: {msg.eval.overallLevel}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-3">
                          {msg.eval.dimensions?.map((dim, i) => (
                            <div key={i} className="bg-white px-4 py-2 rounded-lg shadow-sm border flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700">{dim.name}</span>
                              <span className={`text-lg font-bold ${dim.level === 'A' ? 'text-green-600' : dim.level === 'B' ? 'text-blue-600' : 'text-yellow-600'}`}>
                                {dim.level}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white px-6 py-4 rounded-2xl shadow border border-gray-100 flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                <span className="text-gray-400 text-sm ml-2">AI 正在批改中...</span>
              </div>
            </div>
          )}
        </div>
        {/* 输入框 */}
        {status.active && (
          <form onSubmit={handleSubmit} className="bg-white p-2 rounded-2xl shadow-2xl border border-gray-200 flex items-center">
            <textarea 
              className="flex-1 px-4 py-3 outline-none text-gray-800 placeholder-gray-400 resize-none"
              rows={2}
              value={input} 
              onChange={e => setInput(e.target.value)} 
              placeholder={status.activeTask ? "请在这里输入你的答案..." : "有什么疑问吗？"}
              disabled={loading}
            />
            <button 
              type="submit" 
              disabled={loading || !input.trim()}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 ml-4"
            >
              {loading ? '提交中...' : (status.activeTask ? '提交答案' : '发送')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
export default StudentPanel;